// @PN/inclusao/components/PedidoDialog.tsx
"use client";
import { IconFileCheckFilled, IconSquareCheck, IconSearch } from "@tabler/icons-react";
import type { Pedido } from "@apiInclui/pedido";
import { Badge, Loader, Input } from "visuais";
import { DialogSearch } from "@PN/_nucleo";
import { createPortal } from "react-dom";
import React, { useMemo } from "react";
import { motion } from "motion/react";
import {
  getPedidoKey,
  usePedidoDialog,
  PedidoContentDialog,
  ProdutosHoverCard,
  TriggerCard,
  useCardFlow,
  usePrenotaStore,
} from "@PN/inclusao";

export function PedidoDialog() {
  const {
    open,
    filter,
    selected,
    lista,
    isLoading,
    error,
    canSelectPedidos,
    buttonLabel,
    setOpen,
    setFilter,
    toggle,
    handleKeyDown,
    executeConnectionOnClose,
    preSolicitacao,
    setPreSolicitacao,
    isSearchingPreSol,
    preSolResult,
    buscarPorPreSolicitacao,
  } = usePedidoDialog();
  
  const cardFlow = useCardFlow();
  const pedidoObrigatorio = cardFlow.identificacao.pedidoObrigatorio;
  const pedidosAuxiliares = usePrenotaStore((state) => state.aux.pedidos);

  const [hoveredPedido, setHoveredPedido] = React.useState<Pedido | null>(null);
  const [hoveredElement, setHoveredElement] =
    React.useState<HTMLElement | null>(null);
  const [isHoveringCard, setIsHoveringCard] = React.useState(false);

  // ✅ CORREÇÃO: Limpar hover quando pedido é selecionado ou dialog fecha
  const clearHover = React.useCallback(() => {
    setHoveredPedido(null);
    setHoveredElement(null);
    setIsHoveringCard(false);
  }, []);

  // ✅ GARANTIA: Limpar hover imediatamente quando dialog fechar
  React.useEffect(() => {
    if (!open) {
      clearHover();
    }
  }, [open, clearHover]);

  // ✅ HOVER INTELIGENTE: Só limpa hover se não estiver sobre o card
  const handlePedidoMouseLeave = React.useCallback(() => {
    // Usa setTimeout para dar tempo do mouse entrar no hover card
    setTimeout(() => {
      if (!isHoveringCard) {
        setHoveredPedido(null);
        setHoveredElement(null);
      }
    }, 100); // 100ms de delay
  }, [isHoveringCard]);

  // ✅ HANDLERS DO HOVER CARD
  const handleCardMouseEnter = React.useCallback(() => {
    setIsHoveringCard(true);
  }, []);

  const handleCardMouseLeave = React.useCallback(() => {
    setIsHoveringCard(false);
    // Limpa o hover quando sai do card
    setTimeout(() => {
      setHoveredPedido(null);
      setHoveredElement(null);
    }, 100);
  }, []);

  // Para multiselect, mostra seleção atual e faz fallback no estado auxiliar durante reidratação
  const selectedPedidos = useMemo(() => {
    const selectedFromLista = lista.filter((p) =>
      selected.includes(getPedidoKey(p))
    );
    if (selectedFromLista.length > 0) return selectedFromLista;
    if (pedidosAuxiliares && pedidosAuxiliares.length > 0) return pedidosAuxiliares;
    return [];
  }, [lista, selected, pedidosAuxiliares]);

  // Função auxiliar para verificar se um status indica pendência
  const isPendente = (status: string | undefined): boolean => {
    if (!status) return false;
    const statusUpper = status.toUpperCase().trim();
    return (
      statusUpper === "- PENDENTE" ||
      statusUpper === "PENDENTE" ||
      statusUpper.includes("PENDENTE")    
    );
  };

  // Informações para o card
  const cardInfo = useMemo(() => {
    if (selectedPedidos.length === 0) {
      return {
        label: "Selecionar",
        count: 0,
        hasSelection: false,
      };
    }

    if (selectedPedidos.length === 1) {
      const pedido = selectedPedidos[0];
      return {
        label: pedido.codPedido || "Pedido",
        count: 1,
        hasSelection: true,
        codigo: pedido.codPedido,
        fornecedor: pedido.fornecedor,
        data: pedido.dataEmissao,
        valor: pedido.valorTotal,
        status: pedido.status,
        produtos: pedido.produtos?.length || 0,
        temPendente: isPendente(pedido.status),
      };
    }

    // Para múltiplos pedidos, calcular totais
    const totalProdutos = selectedPedidos.reduce(
      (acc, p) => acc + (p.produtos?.length || 0),
      0
    );
    const valorTotal = selectedPedidos.reduce(
      (acc, p) => acc + (p.valorTotal || 0),
      0
    );
    
    // Verifica se algum pedido está pendente
    const temPendente = selectedPedidos.some((p) => isPendente(p.status));

    return {
      label: `${selectedPedidos.length} pedidos selecionados`,
      count: selectedPedidos.length,
      hasSelection: true,
      totalProdutos,
      valorTotal,
      temPendente,
    };
  }, [selectedPedidos]);

  // Função para selecionar todos os pedidos filtrados
  const handleSelectAll = () => {
    const filteredPedidos = lista.filter((p) => {
      const key = getPedidoKey(p);
      return !selected.includes(key);
    });

    filteredPedidos.forEach((pedido) => {
      toggle(pedido);
    });
  };

  // Função para lidar com a abertura/fechamento do dialog
  const handleDialogOpenChange = (isOpen: boolean) => {
    console.log("🔍 [PedidoDialog] handleDialogOpenChange:", { isOpen });
    setOpen(isOpen);
    // ✅ CORREÇÃO: Limpar hover quando dialog fecha
    if (!isOpen) {
      clearHover();
      console.log(
        "🔍 [PedidoDialog] Dialog fechando, executando conexão automática"
      );
      executeConnectionOnClose();
    }
  };

  // Função para lidar com o fechamento do dialog (confirmação)
  const handleDialogConfirm = () => {
    console.log("🔍 [PedidoDialog] handleDialogConfirm chamado");
    setOpen(false);
    // Executa a conexão automática quando o dialog é fechado
    executeConnectionOnClose();
  };

  const trigger = (
    <TriggerCard
      icon={
        <IconFileCheckFilled className={`size-4 ${pedidoObrigatorio && !cardInfo.hasSelection ? "text-red-600 dark:text-red-400" : "text-cyan-600 dark:text-cyan-400"}`} />
      }
      label="Pedido de Compra"
      isValid={cardInfo.hasSelection}
      isObrigatorio={pedidoObrigatorio}
    >
      <div className="flex flex-col items-center justify-center space-y-1 h-full">
        {/* Badges de informação */}
        {cardInfo.hasSelection && (
          <div className="flex items-center justify-between w-full gap-1">
            {/* Contador de pedidos */}
            <Badge
              variant="secondary"
              className={`flex items-center gap-1 text-[7pt] ${
                cardInfo.temPendente
                  ? "bg-orange-100 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800"
                  : ""
              }`}
            >
              <span>
                {cardInfo.count} pedido{cardInfo.count > 1 ? "s" : ""}
                {cardInfo.temPendente ? " - PENDENTE" : ""}
              </span>
            </Badge>

            {/* Informações adicionais */}
            {cardInfo.count === 1
              ? // Para pedido único, mostrar produtos
                cardInfo.produtos! > 0 && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1 text-[7pt]"
                  >
                    <span>
                      {cardInfo.produtos} produto
                      {cardInfo.produtos! > 1 ? "s" : ""}
                    </span>
                  </Badge>
                )
              : // Para múltiplos pedidos, mostrar total de produtos
                cardInfo.totalProdutos! > 0 && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1 text-[7pt]"
                  >
                    <span>{cardInfo.totalProdutos} produtos</span>
                  </Badge>
                )}
          </div>
        )}

        {/* Nome/descrição principal */}
        <p
          className="text-base uppercase text-center font-semibold text-cyan-800 dark:text-cyan-200 truncate"
          title={cardInfo.label}
        >
          {cardInfo.label}
        </p>
      </div>
    </TriggerCard>
  );

  return (
    <div className="h-full">
      <DialogSearch<Pedido>
        open={open}
        onOpenChange={handleDialogOpenChange}
        term={filter}
        onTermChange={setFilter}
        onKeyDown={handleKeyDown}
        options={lista}
        isLoading={isLoading}
        hasUserTyped={!!filter}
        searchError={error}
        label={buttonLabel}
        disabled={!canSelectPedidos}
        buttonIcon={
          isLoading ? (
            <Loader className="size-5" />
          ) : (
            <IconFileCheckFilled className="size-5 text-muted-foreground" />
          )
        }
        buttonClassName="text-[8pt]"
        placeholder="Busque o pedido de compra para esta pré-nota..."
        current={selectedPedidos.length > 0 ? selectedPedidos : null}
        onSelect={(pedido) => {
          toggle(pedido);
          // ✅ CORREÇÃO: Limpar hover quando pedido é selecionado
          clearHover();
        }}
        groupLabel={(opts) => `${opts.length} pedido(s) encontrado(s)`}
        multiselect={true}
        onConfirm={handleDialogConfirm}
        maxItems={100}
        enableVirtualization={true}
        dialogClassName="min-w-[30vw] overflow-visible !z-[100]"
        virtualizationConfig={{
          estimateSize: 140,
          overscan: 10,
          initialHeight: 600,
        }}
        trigger={trigger}
        headerActions={
          <div className="flex flex-col gap-2 w-full">
            {/* Input de busca por pré solicitação */}
            <div className="flex items-center gap-2 w-full">
              <Input
                type="text"
                value={preSolicitacao}
                onChange={(e) => setPreSolicitacao(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    buscarPorPreSolicitacao(preSolicitacao);
                  }
                }}
                placeholder="Buscar por n. da pré solicitação..."
                className="h-8 text-xs flex-1"
                disabled={isSearchingPreSol}
              />
              <button
                type="button"
                onClick={() => buscarPorPreSolicitacao(preSolicitacao)}
                disabled={isSearchingPreSol || !preSolicitacao.trim()}
                className="flex items-center gap-1.5 px-3 h-8 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors whitespace-nowrap"
              >
                {isSearchingPreSol ? (
                  <Loader className="h-3.5 w-3.5" />
                ) : (
                  <IconSearch className="h-3.5 w-3.5" />
                )}
                Pesquisar
              </button>
              {/* Feedback do resultado */}
              {preSolResult && !preSolResult.found && (
                <span className="text-xs text-red-500 whitespace-nowrap">
                  Nenhum pedido encontrado
                </span>
              )}
              {preSolResult && preSolResult.found && (
                <span className="text-xs text-green-600 whitespace-nowrap">
                  {preSolResult.pedidos.length} pedido(s)
                </span>
              )}
            </div>
          </div>
        }
      >
        {(opt) => {
          const isSelected = selected.includes(getPedidoKey(opt));

          return (
            <div
              onMouseEnter={(e) => {
                setHoveredPedido(opt);
                setHoveredElement(e.currentTarget);
              }}
              onMouseLeave={handlePedidoMouseLeave}
              className="w-full"
            >
              <PedidoContentDialog opt={opt} isSelected={isSelected} />
            </div>
          );
        }}
      </DialogSearch>

      {/* Portal para o Hover Card - Renderizado completamente fora do dialog */}
      {open &&
        hoveredPedido &&
        hoveredPedido.produtos &&
        hoveredPedido.produtos.length > 0 &&
        hoveredElement &&
        createPortal(
          <div
            className="fixed z-[9999] pointer-events-none"
            style={{
              top:
                hoveredElement.getBoundingClientRect().top +
                window.scrollY -
                10,
              left:
                hoveredElement.getBoundingClientRect().right +
                window.scrollX +
                10,
            }}
          >
            <div className="pointer-events-auto">
              <ProdutosHoverCard
                produtos={hoveredPedido.produtos}
                pedidoObservacao={hoveredPedido.observacao}
                isVisible={true}
                onMouseEnter={handleCardMouseEnter}
                onMouseLeave={handleCardMouseLeave}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
